// ============================================================================
// Vehicle status change — the vehicle twin of MachineStatusSheet.
//
// Same interaction, deliberately: a bottom sheet, one option per status, none
// pre-selected beyond the sensible next step, and a single Save. The equipment
// side already taught the fleet-admins this gesture; retiring a van should not
// be a different one.
//
// It differs from the machine sheet in exactly one way, and the difference is
// forced: floor_machines revokes INSERT/UPDATE from `authenticated`, so machine
// status goes through rpc_asset_set_status, which writes a movement event
// alongside the projection. `vehicles` revokes nothing — saveVehicle() already
// writes the table directly under fleet-admin RLS. There is no vehicle movement
// ledger to write to, so inventing an RPC here would add a hop and record
// nothing extra.
//
// THE ONE-FIELD RULE: this writes { status } and nothing else. Retiring a
// vehicle must never require somebody to first fill in a VIN they do not have.
// ============================================================================
import { useState } from 'react';
import { Check, Tag, X } from 'lucide-react';
import { useToast } from '../../shared/components';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { saveVehicle, type VehicleRow } from './hooks/use-fleet-data';
import { VEHICLE_STATUSES, isInactiveVehicleStatus, vehicleStatusLabel } from './vehicle-status';

const sheetCls =
  'fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4';
const panelCls =
  'max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg sm:max-w-lg sm:rounded-2xl';
const bigBtn =
  'flex min-h-[3.25rem] flex-1 items-center justify-center gap-2 rounded-xl px-3 text-base font-semibold disabled:opacity-60';

export function VehicleStatusSheet({
  vehicle,
  onClose,
  onSaved,
}: {
  vehicle: VehicleRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error } = useToast();
  const current = (vehicle.status ?? '').trim();
  // The options actually on offer: the vocabulary minus whatever it already is.
  // An off-list legacy value (the live 'Active - Reg Needed' row) is therefore
  // never in this list and cannot be re-selected — but it is not rewritten
  // either. It stays until somebody deliberately picks something else.
  const options = VEHICLE_STATUSES.filter((s) => s !== current);
  const [status, setStatus] = useState<string>(options[0] ?? VEHICLE_STATUSES[0]);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await saveVehicle(vehicle.vehicle_id, { status });
      success(`${vehicle.vehicle_id} is now ${vehicleStatusLabel(status)}.`);
      onSaved();
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not change the status.'));
    } finally {
      setSaving(false);
    }
  }

  const goingInactive = isInactiveVehicleStatus(status);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${vehicle.vehicle_id} status`}
      className={sheetCls}
      {...overlayClickClose(onClose)}
    >
      <div className={panelCls} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-tcs-text">{vehicle.vehicle_id} status</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-sm text-tcs-text-secondary">
          Currently <span className="font-medium text-tcs-text">{vehicleStatusLabel(vehicle.status)}</span>.
        </p>

        <div className="space-y-2">
          {options.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-base ${
                status === s
                  ? 'border-tcs-border-input bg-tcs-accent-bg font-semibold text-tcs-text'
                  : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary'
              }`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  status === s ? 'border-tcs-inverse bg-tcs-inverse' : 'border-tcs-border-strong'
                }`}
              >
                {status === s && <Check className="h-2.5 w-2.5 text-tcs-inverse-ink" />}
              </span>
              {s}
            </button>
          ))}
        </div>

        {goingInactive && (
          <p className="mt-3 rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2 text-[12.5px] text-st-info-fg-strong">
            This takes {vehicle.vehicle_id} out of the default fleet list. Nothing is deleted — its
            costs, mileage history and this page all stay, and it comes back into the list with
            “Show sold &amp; retired”.
          </p>
        )}

        <div className="mt-4 flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[3.25rem] rounded-xl border border-tcs-border bg-tcs-raised px-4 text-base text-tcs-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className={`${bigBtn} bg-tcs-inverse text-tcs-inverse-ink`}
          >
            <Tag className="h-4 w-4" />
            {saving ? 'Saving…' : `Set ${vehicleStatusLabel(status)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
